import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

const ASSET_TYPES = ['OmniBanner', 'Immersive Frames', 'Sponsored Stream', 'Chatbot Panel', 'Pre-Roll', 'Other'];
const fmtNum = (n) => n ? parseInt(n).toLocaleString() : '0';

const CampaignReport = ({ dealId, deal }) => {
  const [reports, setReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [periods, setPeriods] = useState([]);
  const periodsRef = useRef(periods);
  // Keep ref in sync
  useEffect(() => { periodsRef.current = periods; }, [periods]);
  const [screenshots, setScreenshots] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [activePeriodId, setActivePeriodId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showScreenshots, setShowScreenshots] = useState(false);
  const [linkedTalent, setLinkedTalent] = useState([]);
  const fileRef = useRef();

  const [reportForm, setReportForm] = useState({
    brand_name: deal?.account || '', agency: '', campaign_name: deal?.name || '',
    flight_start: '', flight_end: '', impressions_goal: '',
    nextide_contact: '', nextide_email: ''
  });
  const [periodForm, setPeriodForm] = useState({ period_name: '', reporting_start: '', reporting_end: '' });

  useEffect(() => { fetchReports(); fetchLinkedTalent(); }, [dealId]);

  const fetchReports = async () => {
    try {
      const res = await api.get(`/deals/${dealId}/reports`);
      setReports(res.data.reports || []);
    } catch (e) {}
  };

  const fetchLinkedTalent = async () => {
    try {
      const res = await api.get(`/deals/${dealId}/talent-list`);
      setLinkedTalent(res.data.items || []);
    } catch (e) {}
  };

  const loadReport = async (reportId) => {
    try {
      const res = await api.get(`/reports/${reportId}`);
      setActiveReport(res.data.report);
      setPeriods(res.data.periods || []);
      setScreenshots(res.data.screenshots || []);
      if (res.data.periods.length > 0) setActivePeriodId(res.data.periods[res.data.periods.length - 1].id);
    } catch (e) { alert('Failed to load report'); }
  };

  const createReport = async () => {
    if (!reportForm.campaign_name) return;
    try {
      const res = await api.post(`/deals/${dealId}/reports`, reportForm);
      setShowCreate(false);
      fetchReports();
      loadReport(res.data.report.id);
    } catch (e) { alert('Failed'); }
  };

  const handleExport = async () => {
    if (!activeReport) return;
    setExporting(true);
    try {
      const res = await api.get(`/reports/${activeReport.id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${activeReport.campaign_name.replace(/[^a-zA-Z0-9 ]/g, '')}_Report.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { alert('Export failed'); }
    finally { setExporting(false); }
  };

  const addPeriod = async () => {
    if (!periodForm.period_name || !activeReport) return;
    try {
      const res = await api.post(`/reports/${activeReport.id}/periods`, periodForm);
      setPeriodForm({ period_name: '', reporting_start: '', reporting_end: '' });
      loadReport(activeReport.id);
      setActivePeriodId(res.data.period.id);
    } catch (e) { alert('Failed'); }
  };

  const duplicatePeriod = async (periodId) => {
    const name = prompt('New period name?');
    if (!name) return;
    try {
      await api.post(`/reports/periods/${periodId}/duplicate`, { new_period_name: name });
      loadReport(activeReport.id);
    } catch (e) { alert('Failed'); }
  };

  const deletePeriod = async (periodId) => {
    if (!window.confirm('Delete this period and all its data?')) return;
    try { await api.delete(`/reports/periods/${periodId}`); loadReport(activeReport.id); } catch (e) {}
  };

  const addEntry = async (periodId) => {
    const firstTalent = linkedTalent.length > 0 ? linkedTalent[0] : null;
    try {
      await api.post(`/reports/periods/${periodId}/entries`, {
        creator_name: firstTalent ? firstTalent.name : 'New Creator',
        talent_id: firstTalent ? firstTalent.talent_id : null,
        asset_type: 'OmniBanner'
      });
      loadReport(activeReport.id);
    } catch (e) { alert('Failed'); }
  };

  const updateEntryLocal = (entryId, field, value) => {
    setPeriods(prev => prev.map(p => ({
      ...p,
      entries: p.entries.map(e => e.id === entryId ? { ...e, [field]: value } : e)
    })));
  };

  const saveEntry = async (entryId) => {
    const currentPeriods = periodsRef.current;
    const period = currentPeriods.find(p => p.entries.some(e => e.id === entryId));
    const entry = period?.entries.find(e => e.id === entryId);
    if (!entry) return;
    try { await api.put(`/reports/entries/${entryId}`, entry); } catch (e) {}
  };

  const deleteEntry = async (entryId) => {
    try { await api.delete(`/reports/entries/${entryId}`); loadReport(activeReport.id); } catch (e) {}
  };

  const uploadScreenshot = async (e, creatorName, assetType) => {
    const file = e.target.files[0];
    if (!file || !activeReport) return;
    const formData = new FormData();
    formData.append('image', file);
    formData.append('creator_name', creatorName);
    formData.append('asset_type', assetType);
    try {
      await api.post(`/reports/${activeReport.id}/screenshots`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadReport(activeReport.id);
    } catch (e) { alert('Upload failed'); }
  };

  const deleteScreenshot = async (ssId) => {
    try { await api.delete(`/reports/screenshots/${ssId}`); loadReport(activeReport.id); } catch (e) {}
  };

  const activePeriod = periods.find(p => p.id === activePeriodId);

  // Compute totals for active period
  const periodTotals = (entries) => {
    const impr = entries.reduce((s, e) => s + (parseInt(e.impressions) || 0), 0);
    const eng = entries.reduce((s, e) => s + (parseInt(e.engagements) || 0), 0);
    const clicks = entries.reduce((s, e) => s + (parseInt(e.clicks) || 0), 0);
    const ccvEntries = entries.filter(e => parseInt(e.avg_ccv) > 0);
    const avgCCV = ccvEntries.length > 0 ? Math.round(ccvEntries.reduce((s, e) => s + parseInt(e.avg_ccv), 0) / ccvEntries.length) : 0;
    return { impr, eng, clicks, avgCCV, er: impr > 0 ? eng / impr : 0, ctr: impr > 0 ? clicks / impr : 0 };
  };

  // If no active report, show report list
  if (!activeReport) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Campaign Reports</h3>
          <button onClick={() => setShowCreate(true)} className="bg-nextide-turquoise text-black px-3 py-1.5 rounded-lg text-sm font-semibold">+ New Report</button>
        </div>

        {reports.length === 0 && !showCreate && (
          <div className="text-zinc-500 text-sm py-8 text-center">No reports yet. Create one to start tracking campaign performance.</div>
        )}

        {reports.map(r => (
          <div key={r.id} onClick={() => loadReport(r.id)}
            className="bg-zinc-800 rounded-lg p-4 mb-2 cursor-pointer hover:bg-zinc-700 transition-colors">
            <div className="text-white font-medium">{r.campaign_name}</div>
            <div className="text-zinc-500 text-xs mt-1">{r.brand_name} | {new Date(r.created_at).toLocaleDateString()}</div>
          </div>
        ))}

        {showCreate && (
          <div className="bg-zinc-800 rounded-xl p-5 mt-4">
            <h4 className="text-white font-semibold mb-3">New Campaign Report</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs text-zinc-400 mb-1">Campaign Name *</label>
                <input type="text" value={reportForm.campaign_name} onChange={e => setReportForm({...reportForm, campaign_name: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">Brand Name</label>
                <input type="text" value={reportForm.brand_name} onChange={e => setReportForm({...reportForm, brand_name: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">Agency</label>
                <input type="text" value={reportForm.agency} onChange={e => setReportForm({...reportForm, agency: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">Impressions Goal</label>
                <input type="number" value={reportForm.impressions_goal} onChange={e => setReportForm({...reportForm, impressions_goal: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">Flight Start</label>
                <input type="date" value={reportForm.flight_start} onChange={e => setReportForm({...reportForm, flight_start: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">Flight End</label>
                <input type="date" value={reportForm.flight_end} onChange={e => setReportForm({...reportForm, flight_end: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">NexTide Contact</label>
                <input type="text" value={reportForm.nextide_contact} onChange={e => setReportForm({...reportForm, nextide_contact: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div><label className="block text-xs text-zinc-400 mb-1">Contact Email</label>
                <input type="text" value={reportForm.nextide_email} onChange={e => setReportForm({...reportForm, nextide_email: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowCreate(false)} className="bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={createReport} disabled={!reportForm.campaign_name}
                className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">Create Report</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const cumImpr = periods.reduce((s, p) => s + (p.entries || []).reduce((ss, e) => ss + (parseInt(e.impressions) || 0), 0), 0);
  const goalPct = activeReport.impressions_goal > 0 ? (cumImpr / activeReport.impressions_goal * 100).toFixed(1) : 'N/A';

  return (
    <div>
      {/* Report Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={() => { setActiveReport(null); setPeriods([]); }} className="text-zinc-400 hover:text-white text-xs mb-1 block">← All Reports</button>
          <h3 className="text-white font-semibold text-lg">{activeReport.campaign_name}</h3>
          <div className="flex gap-4 text-xs text-zinc-400 mt-1">
            <span>{activeReport.brand_name}</span>
            {activeReport.agency && <span>{activeReport.agency}</span>}
            <span className={`font-semibold ${parseFloat(goalPct) >= 100 ? 'text-green-400' : 'text-yellow-400'}`}>{goalPct}% to goal</span>
            <span>{fmtNum(cumImpr)} / {fmtNum(activeReport.impressions_goal)} impressions</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowScreenshots(!showScreenshots)}
            className={`px-3 py-1.5 rounded-lg text-sm ${showScreenshots ? 'bg-zinc-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>Screenshots ({screenshots.length})</button>
          <button onClick={async () => {
            if (!window.confirm('Delete this entire report and all its data?')) return;
            try { await api.delete(`/reports/${activeReport.id}`); setActiveReport(null); setPeriods([]); fetchReports(); } catch(e) { alert('Failed'); }
          }} className="bg-zinc-800 text-red-400 px-3 py-1.5 rounded-lg text-sm hover:bg-red-500/20">Delete</button>
          <button onClick={handleExport} disabled={exporting || periods.length === 0}
            className="bg-nextide-turquoise text-black px-4 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50">{exporting ? 'Exporting...' : 'Export .xlsx'}</button>
        </div>
      </div>

      {/* Screenshots Panel */}
      {showScreenshots && (
        <div className="bg-zinc-800 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-semibold text-sm">Placement Screenshots</h4>
            <label className="bg-zinc-700 text-white px-3 py-1 rounded text-xs cursor-pointer hover:bg-zinc-600">
              Upload
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                const creator = prompt('Creator name for this screenshot?');
                const asset = prompt('Asset type? (OmniBanner, Immersive Frames, Sponsored Stream, etc)');
                if (creator && asset) uploadScreenshot(e, creator, asset);
              }} />
            </label>
          </div>
          {screenshots.length === 0 ? (
            <div className="text-zinc-500 text-sm">No screenshots uploaded</div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {screenshots.map(ss => (
                <div key={ss.id} className="flex items-center justify-between bg-zinc-700 rounded px-3 py-2">
                  <div className="text-sm"><span className="text-white font-medium">{ss.creator_name}</span> <span className="text-zinc-400">| {ss.asset_type}</span> <span className="text-zinc-500 text-xs ml-2">{ss.filename}</span></div>
                  <button onClick={() => deleteScreenshot(ss.id)} className="text-zinc-500 hover:text-red-400 text-xs">x</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Period Tabs */}
      <div className="flex items-center gap-1 mb-2 border-b border-zinc-800 overflow-x-auto">
        {periods.map(p => (
          <button key={p.id} onClick={() => setActivePeriodId(p.id)}
            className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 ${activePeriodId === p.id ? 'text-nextide-turquoise border-nextide-turquoise font-medium' : 'text-zinc-400 border-transparent hover:text-white'}`}>
            {p.period_name}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-4">
        <input type="text" placeholder="e.g. January Wk 1-2" value={periodForm.period_name}
          onChange={e => setPeriodForm({...periodForm, period_name: e.target.value})}
          onKeyDown={e => e.key === 'Enter' && addPeriod()}
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm w-52" />
        <button onClick={addPeriod} disabled={!periodForm.period_name}
          className="bg-nextide-turquoise text-black px-4 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-30">
          + Add Period
        </button>
      </div>

      {/* Active Period */}
      {activePeriod ? (
        <div>
          {/* Period meta bar */}
          <div className="flex items-center gap-3 mb-3 text-xs">
            <span className="text-zinc-500">Dates:</span>
            <input type="date" value={activePeriod.reporting_start?.split('T')[0] || ''}
              onChange={e => setPeriods(prev => prev.map(p => p.id === activePeriod.id ? {...p, reporting_start: e.target.value} : p))}
              onBlur={e => api.put(`/reports/periods/${activePeriod.id}`, { ...activePeriod, reporting_start: e.target.value })}
              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs" />
            <span className="text-zinc-600">to</span>
            <input type="date" value={activePeriod.reporting_end?.split('T')[0] || ''}
              onChange={e => setPeriods(prev => prev.map(p => p.id === activePeriod.id ? {...p, reporting_end: e.target.value} : p))}
              onBlur={e => api.put(`/reports/periods/${activePeriod.id}`, { ...activePeriod, reporting_end: e.target.value })}
              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs" />
            <div className="ml-auto flex gap-2">
              <button onClick={async () => {
                if (!window.confirm(`Finalize "${activePeriod.period_name}"? This will create campaign history entries for each creator.`)) return;
                try {
                  const res = await api.post(`/reports/periods/${activePeriod.id}/finalize`);
                  alert(`Finalized! Updated ${res.data.upserted} creator campaign histories (${res.data.total_creators} total creators, aggregated across all periods).`);
                } catch (e) { alert('Failed to finalize'); }
              }} className="text-nextide-turquoise hover:underline text-xs font-semibold">Finalize Period</button>
              <button onClick={() => duplicatePeriod(activePeriod.id)} className="text-zinc-400 hover:text-white text-xs">Duplicate</button>
              <button onClick={() => deletePeriod(activePeriod.id)} className="text-zinc-400 hover:text-red-400 text-xs">Delete Period</button>
            </div>
          </div>

          {/* Entries table */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-zinc-700">
                  {['Creator', 'Asset Type', 'Impressions', 'Ad Viewability', 'Avg CCV', 'Engagements', 'Eng. Rate', 'Clicks', 'CTR', ''].map(h => (
                    <th key={h} className="text-left p-2 text-zinc-400 text-xs font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(activePeriod.entries || []).map((entry, idx) => (
                  <tr key={entry.id} className={`border-b border-zinc-800/50 ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800/30'}`}>
                    <td className="p-2">
                      {linkedTalent.length > 0 ? (
                        <select value={entry.talent_id ? `${entry.talent_id}|${entry.creator_name}` : `custom|${entry.creator_name}`}
                          onChange={e => {
                            const [tid, ...nameParts] = e.target.value.split('|');
                            const name = nameParts.join('|');
                            if (tid === 'custom') {
                              const customName = prompt('Creator name (e.g. "JeromeASF x Tewtiy"):');
                              if (customName) {
                                updateEntryLocal(entry.id, 'creator_name', customName);
                                updateEntryLocal(entry.id, 'talent_id', null);
                                setTimeout(() => saveEntry(entry.id), 50);
                              }
                            } else {
                              updateEntryLocal(entry.id, 'creator_name', name);
                              updateEntryLocal(entry.id, 'talent_id', parseInt(tid));
                              setTimeout(() => saveEntry(entry.id), 50);
                            }
                          }}
                          className="bg-transparent text-white text-sm font-medium w-full focus:outline-none cursor-pointer [&>option]:bg-white [&>option]:text-black">
                          {linkedTalent.map(t => (
                            <option key={t.talent_id} value={`${t.talent_id}|${t.name}`}>{t.name}</option>
                          ))}
                          {entry.talent_id === null && entry.creator_name !== 'New Creator' && (
                            <option value={`custom|${entry.creator_name}`}>{entry.creator_name} (custom)</option>
                          )}
                          <option value="custom|">+ Custom creator...</option>
                        </select>
                      ) : (
                        <input type="text" value={entry.creator_name} onChange={e => updateEntryLocal(entry.id, 'creator_name', e.target.value)}
                          onBlur={() => saveEntry(entry.id)}
                          className="bg-transparent text-white text-sm font-medium w-full focus:outline-none focus:bg-zinc-800 rounded px-1" />
                      )}
                    </td>
                    <td className="p-2"><select value={entry.asset_type} onChange={e => { updateEntryLocal(entry.id, 'asset_type', e.target.value); setTimeout(() => saveEntry(entry.id), 50); }}
                      className="bg-transparent text-zinc-300 text-xs w-full focus:outline-none cursor-pointer [&>option]:bg-white [&>option]:text-black">
                      {ASSET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select></td>
                    <td className="p-2"><input type="number" value={entry.impressions || ''} onChange={e => updateEntryLocal(entry.id, 'impressions', e.target.value)} onBlur={() => saveEntry(entry.id)}
                      className="bg-transparent text-white text-xs w-20 focus:outline-none focus:bg-zinc-800 rounded px-1" placeholder="0" /></td>
                    <td className="p-2"><input type="number" step="1" value={entry.ad_viewability || ''} onChange={e => updateEntryLocal(entry.id, 'ad_viewability', e.target.value)} onBlur={() => saveEntry(entry.id)}
                      className="bg-transparent text-white text-xs w-14 focus:outline-none focus:bg-zinc-800 rounded px-1" placeholder="100" /></td>
                    <td className="p-2"><input type="number" value={entry.avg_ccv || ''} onChange={e => updateEntryLocal(entry.id, 'avg_ccv', e.target.value)} onBlur={() => saveEntry(entry.id)}
                      className="bg-transparent text-purple-400 text-xs w-14 focus:outline-none focus:bg-zinc-800 rounded px-1" placeholder="0" /></td>
                    <td className="p-2"><input type="number" value={entry.engagements || ''} onChange={e => updateEntryLocal(entry.id, 'engagements', e.target.value)} onBlur={() => saveEntry(entry.id)}
                      className="bg-transparent text-white text-xs w-16 focus:outline-none focus:bg-zinc-800 rounded px-1" placeholder="0" /></td>
                    <td className="p-2 text-zinc-400 text-xs">{parseInt(entry.impressions) > 0 && parseInt(entry.engagements) > 0 ? (parseInt(entry.engagements) / parseInt(entry.impressions) * 100).toFixed(4) + '%' : 'N/A'}</td>
                    <td className="p-2"><input type="number" value={entry.clicks || ''} onChange={e => updateEntryLocal(entry.id, 'clicks', e.target.value)} onBlur={() => saveEntry(entry.id)}
                      className="bg-transparent text-white text-xs w-14 focus:outline-none focus:bg-zinc-800 rounded px-1" placeholder="0" /></td>
                    <td className="p-2 text-zinc-400 text-xs">{parseInt(entry.impressions) > 0 && parseInt(entry.clicks) > 0 ? (parseInt(entry.clicks) / parseInt(entry.impressions) * 100).toFixed(4) + '%' : 'N/A'}</td>
                    <td className="p-2"><button onClick={() => deleteEntry(entry.id)} className="text-zinc-600 hover:text-red-400 text-xs">x</button></td>
                  </tr>
                ))}

                {/* Totals row */}
                {(activePeriod.entries || []).length > 0 && (() => {
                  const t = periodTotals(activePeriod.entries || []);
                  return (
                    <tr className="border-t-2 border-nextide-turquoise">
                      <td className="p-2 text-nextide-turquoise text-xs font-bold">Totals</td>
                      <td className="p-2"></td>
                      <td className="p-2 text-nextide-turquoise text-xs font-bold">{fmtNum(t.impr)}</td>
                      <td className="p-2 text-nextide-turquoise text-xs">{(() => { const vEntries = (activePeriod.entries||[]).filter(e => parseFloat(e.ad_viewability) > 0); return vEntries.length > 0 ? Math.round(vEntries.reduce((s,e) => s + parseFloat(e.ad_viewability), 0) / vEntries.length) + '%' : 'N/A'; })()}</td>
                      <td className="p-2 text-purple-400 text-xs font-bold">{t.avgCCV > 0 ? fmtNum(t.avgCCV) : 'N/A'}</td>
                      <td className="p-2 text-nextide-turquoise text-xs font-bold">{fmtNum(t.eng)}</td>
                      <td className="p-2 text-nextide-turquoise text-xs font-bold">{t.impr > 0 ? (t.er * 100).toFixed(4) + '%' : 'N/A'}</td>
                      <td className="p-2 text-nextide-turquoise text-xs font-bold">{fmtNum(t.clicks)}</td>
                      <td className="p-2 text-nextide-turquoise text-xs font-bold">{t.impr > 0 ? (t.ctr * 100).toFixed(4) + '%' : 'N/A'}</td>
                      <td></td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>

          <button onClick={() => addEntry(activePeriod.id)}
            className="mt-3 text-nextide-turquoise text-sm hover:underline">+ Add Row</button>
        </div>
      ) : (
        <div className="text-zinc-500 text-sm py-8 text-center">Add a reporting period to get started</div>
      )}
    </div>
  );
};

export default CampaignReport;
